import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Plus, Trash2, Check, X } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import api from '../utils/api';

const formatDKK = (val) => val.toLocaleString('da-DK');
const formatDate = (d) => d || '—';

const DateCell = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <input
        type="date"
        defaultValue={value || ''}
        autoFocus
        onBlur={(e) => { onChange(e.target.value); setEditing(false); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { onChange(e.target.value); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
        className="bg-white/10 border border-[#1E6F7A]/50 rounded px-2 py-0.5 text-white text-xs focus:outline-none focus:border-[#1E6F7A] w-32"
      />
    );
  }

  return (
    <button onClick={() => setEditing(true)} className="text-xs tabular-nums hover:text-[#1E6F7A] transition-colors cursor-pointer">
      {value ? <span className="text-white/70">{value}</span> : <span className="text-white/20">Ikke sat</span>}
    </button>
  );
};

const NumberCell = ({ value, onChange, suffix }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');

  if (editing) {
    return (
      <input
        type="number"
        step="0.5"
        defaultValue={value || ''}
        autoFocus
        onBlur={(e) => { onChange(parseFloat(e.target.value) || 0); setEditing(false); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { onChange(parseFloat(e.target.value) || 0); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
        className="bg-white/10 border border-[#1E6F7A]/50 rounded px-2 py-0.5 text-white text-xs text-right focus:outline-none focus:border-[#1E6F7A] w-16"
      />
    );
  }

  return (
    <button onClick={() => setEditing(true)} className="text-xs tabular-nums hover:text-[#1E6F7A] transition-colors cursor-pointer">
      {value ? <span className="text-white/70">{value}{suffix}</span> : <span className="text-white/20">Ikke sat</span>}
    </button>
  );
};

const SalaryTable = ({ employees, onDelete, onAdd, onUpdate, showHours }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSalary, setNewSalary] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newHours, setNewHours] = useState('');

  const handleAdd = () => {
    const salary = parseInt(newSalary, 10);
    if (!newName.trim() || isNaN(salary) || salary <= 0) return;
    onAdd(newName.trim(), salary, newStart, newEnd, parseFloat(newHours) || 0);
    setNewName(''); setNewSalary(''); setNewStart(''); setNewEnd(''); setNewHours('');
    setShowAdd(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAdd();
    if (e.key === 'Escape') setShowAdd(false);
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <button data-testid="add-employee-btn" onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E6F7A]/20 hover:bg-[#1E6F7A]/30 text-[#1E6F7A] rounded text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Tilføj medarbejder
        </button>
      </div>

      {showAdd && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 flex items-end gap-3 flex-wrap" data-testid="add-employee-form">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-white/50 text-xs mb-1">Navn</label>
            <input data-testid="new-employee-name" type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown} autoFocus placeholder="Fulde navn"
              className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          <div className="w-32">
            <label className="block text-white/50 text-xs mb-1">Månedsløn</label>
            <input data-testid="new-employee-salary" type="number" value={newSalary} onChange={(e) => setNewSalary(e.target.value)}
              onKeyDown={handleKeyDown} placeholder="kr."
              className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm text-right focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          <div className="w-36">
            <label className="block text-white/50 text-xs mb-1">Startdato</label>
            <input type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)}
              className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          <div className="w-36">
            <label className="block text-white/50 text-xs mb-1">Slutdato</label>
            <input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)}
              className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#1E6F7A]" />
          </div>
          {showHours && (
            <div className="w-24">
              <label className="block text-white/50 text-xs mb-1">Timer/uge</label>
              <input type="number" step="0.5" value={newHours} onChange={(e) => setNewHours(e.target.value)}
                onKeyDown={handleKeyDown} placeholder="t/uge"
                className="w-full bg-white/10 border border-white/10 rounded px-3 py-1.5 text-white text-sm text-right focus:outline-none focus:border-[#1E6F7A]" />
            </div>
          )}
          <button onClick={handleAdd} data-testid="confirm-add-employee"
            className="p-2 bg-[#1E6F7A] hover:bg-[#1E6F7A]/80 text-white rounded transition-colors">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={() => setShowAdd(false)}
            className="p-2 bg-white/5 hover:bg-white/10 text-white/60 rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-[#111318] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full" data-testid="salary-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Navn</th>
              <th className="text-right text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Månedsløn</th>
              {showHours && <th className="text-center text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Timer/uge</th>}
              <th className="text-center text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Startdato</th>
              <th className="text-center text-white/50 text-xs font-medium px-4 py-3 uppercase tracking-wider">Slutdato</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp, idx) => (
              <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`salary-row-${idx}`}>
                <td className="px-4 py-3 text-white text-sm font-medium">{emp.name}</td>
                <td className="px-4 py-3 text-right text-[#1E6F7A] text-sm tabular-nums">{formatDKK(emp.salary)} kr.</td>
                {showHours && (
                  <td className="px-4 py-3 text-center">
                    <NumberCell value={emp.weekly_hours} onChange={(v) => onUpdate(emp.name, { weekly_hours: v })} suffix=" t" />
                  </td>
                )}
                <td className="px-4 py-3 text-center">
                  <DateCell value={emp.start_date} onChange={(v) => onUpdate(emp.name, { start_date: v })} />
                </td>
                <td className="px-4 py-3 text-center">
                  <DateCell value={emp.end_date} onChange={(v) => onUpdate(emp.name, { end_date: v })} />
                </td>
                <td className="px-4 py-3 text-center">
                  <button data-testid={`delete-employee-${idx}`} onClick={() => {
                    if (window.confirm(`Er du sikker på at du vil slette ${emp.name}?`)) onDelete(emp.name);
                  }}
                    className="p-1 rounded hover:bg-red-400/10 text-white/20 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            <tr className="bg-white/5">
              <td className="px-4 py-3 text-white text-sm font-semibold">I alt</td>
              <td className="px-4 py-3 text-right text-white text-sm font-semibold tabular-nums">
                {formatDKK(employees.reduce((s, e) => s + e.salary, 0))} kr.
              </td>
              {showHours && <td></td>}
              <td colSpan={3}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

// ============ PROVISION TABLE ============
const MAANEDER = [
  { key: 'jan', label: 'Jan' },
  { key: 'feb', label: 'Feb' },
  { key: 'mar', label: 'Mar' },
  { key: 'apr', label: 'Apr' },
  { key: 'maj', label: 'Maj' },
  { key: 'jun', label: 'Jun' },
  { key: 'jul', label: 'Jul' },
  { key: 'aug', label: 'Aug' },
  { key: 'sep', label: 'Sep' },
  { key: 'okt', label: 'Okt' },
  { key: 'nov', label: 'Nov' },
  { key: 'dec', label: 'Dec' },
];

// Normaliser navne til opslag (fjern punktum, ekstra mellemrum, lowercase)
const normalizeName = (s) => (s || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();

// Reducér til fornavn + efternavn (ignorer mellemnavne) for fleksibelt match
const shortKey = (s) => {
  const parts = normalizeName(s).split(' ').filter(Boolean);
  if (parts.length <= 1) return parts[0] || '';
  return parts[0] + ' ' + parts[parts.length - 1];
};

// Manuelle aliasser — mapper DATABASE-navn → key i PROVISION_REALISERET_FULL
const NAME_ALIASES = {
  'jakob berg': 'jacob berg',                                  // db har Jakob med K, min data har Jacob med C
  'lennarth søndergaard': 'lennarth falk-søndergaard',         // bindestreg i efternavn forhindrer shortKey-match
  'lisette pokorny': 'lisette rosendal pokorni',               // db har Pokorny med Y, min data har Pokorni med I
};

// Realiseret provision pr. konsulent pr. måned. Key = normaliseret navn, value = { måned-key: beløb }
const PROVISION_REALISERET_FULL = {
  [normalizeName('Kenneth Andersen')]:               { apr: 0 },
  [normalizeName('Jacob Berg')]:                     { apr: 28623.23 },
  [normalizeName('Nikolaj Jensen Blichfeldt')]:      { apr: 39348.12 },
  [normalizeName('Oskar Daniel Ducic')]:             { apr: 0 },
  [normalizeName('Lennarth Falk-Søndergaard')]:      { apr: 29578.06 },
  [normalizeName('Oliver Guldbrandsen')]:            { apr: 23888.80 },
  [normalizeName('Emil Henrik Folke Hansen')]:       { apr: 28908.42 },
  [normalizeName('Sebastian Havemann')]:             { apr: 16945.13 },
  [normalizeName('Lasse Høybye')]:                   { apr: 25024.21 },
  [normalizeName('Christopher Kaae Iversen')]:       { apr: 30617.59 },
  [normalizeName('Emil Methinus Jensen')]:           { apr: 20351.01 },
  [normalizeName('Oscar Landau')]:                   { apr: 15445.04 },
  [normalizeName('René Lantow')]:                    { apr: 36491.19 },
  [normalizeName('Allan Oldenborg Lundbek')]:        { apr: 75955.30 },
  [normalizeName('Benny Pedersen')]:                 { apr: 18828.67 },
  [normalizeName('Lisette Rosendal Pokorni')]:       { apr: 806.89 },
  [normalizeName('Noah Vinzent Richter')]:           { apr: 48228.92 },
  [normalizeName('Martin Schou')]:                   { apr: 37000.58 },
  [normalizeName('Mikkel Erik Heino Thomsen')]:      { apr: 22717.91 },
  [normalizeName('Tobias Zederkoff')]:               { apr: 33426.35 },
};

// Byg ekstra lookup-map med korte (fornavn + efternavn) keys, så mellemnavne ignoreres
const PROVISION_REALISERET_SHORT = Object.entries(PROVISION_REALISERET_FULL).reduce((acc, [fullKey, data]) => {
  acc[shortKey(fullKey)] = data;
  return acc;
}, {});

// Slå data op for en konsulent med flere fallback-strategier
const lookupProvision = (consultantName) => {
  const full = normalizeName(consultantName);
  if (PROVISION_REALISERET_FULL[full]) return PROVISION_REALISERET_FULL[full];
  // Tjek alias
  const aliased = NAME_ALIASES[full] || NAME_ALIASES[shortKey(consultantName)];
  if (aliased && PROVISION_REALISERET_FULL[aliased]) return PROVISION_REALISERET_FULL[aliased];
  // Fald tilbage til fornavn+efternavn match
  const short = shortKey(consultantName);
  if (PROVISION_REALISERET_SHORT[short]) return PROVISION_REALISERET_SHORT[short];
  // Tjek alias-mapped short
  const aliasedShort = NAME_ALIASES[short];
  if (aliasedShort && PROVISION_REALISERET_SHORT[shortKey(aliasedShort)]) {
    return PROVISION_REALISERET_SHORT[shortKey(aliasedShort)];
  }
  return {};
};

// Realiseret freelancer-løn pr. måned (manuelt indtastet — udvides over tid)
const FREELANCER_REALISERET = {
  [normalizeName('Ole Richardsen')]: { apr: 40500 },
  [normalizeName('Mika Hansen')]:    { apr: 49102 },
  [normalizeName('Jakob Lærke')]:    { apr: 35830 },
  [normalizeName('Frence Touch')]:   { apr: 86250 },
};

const FREELANCER_REALISERET_SHORT = Object.entries(FREELANCER_REALISERET).reduce((acc, [fullKey, data]) => {
  acc[shortKey(fullKey)] = data;
  return acc;
}, {});

// Realiseret provision pr. montør pr. måned — manuelt indtastet (udvides over tid)
const MONTOER_PROVISION_REALISERET_FULL = {
  [normalizeName('Adrian Ørts')]:          { apr: 0 },
  [normalizeName('Terkel Gredsted')]:      { apr: 0 },
  [normalizeName('Malthe Hvidholm')]:      { apr: 0 },
  [normalizeName('Jakob Jørgensen')]:      { apr: 0 },
  [normalizeName('David Lindberg Hansen')]:{ apr: 0 },
  [normalizeName('Johanes Brion')]:        { apr: 0 },
  [normalizeName('Lauge Møller')]:         { apr: 0 },
};

const MONTOER_PROVISION_REALISERET_SHORT = Object.entries(MONTOER_PROVISION_REALISERET_FULL).reduce((acc, [fullKey, data]) => {
  acc[shortKey(fullKey)] = data;
  return acc;
}, {});

const lookupMontoerProvision = (name) => {
  const full = normalizeName(name);
  if (MONTOER_PROVISION_REALISERET_FULL[full]) return MONTOER_PROVISION_REALISERET_FULL[full];
  const short = shortKey(name);
  if (MONTOER_PROVISION_REALISERET_SHORT[short]) return MONTOER_PROVISION_REALISERET_SHORT[short];
  return {};
};

const formatProvisionAmount = (v) => {
  if (v === null || v === undefined) return null;
  if (v === 0) return '0';
  return v.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ProvisionTable = ({ konsulenter, loading, entityLabel = 'konsulenter', budgetData = null, realisertData = null }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <div className="bg-[#13151a] rounded-xl border border-white/5 overflow-auto" data-testid="provision-table">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-white/[0.02] border-b border-white/10">
            <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-white/40 font-semibold sticky left-0 bg-[#141517] z-10 min-w-[180px]">{entityLabel === 'montører' ? 'Montør' : 'Konsulent'}</th>
            {MAANEDER.map(m => (
              <th key={m.key} colSpan={2} className="px-2 py-2.5 text-center text-[10px] uppercase tracking-wider text-white/40 font-semibold border-l border-white/[0.06]">{m.label}</th>
            ))}
          </tr>
          <tr className="bg-white/[0.02] border-b border-white/10">
            <th className="px-4 py-1.5 sticky left-0 bg-[#141517] z-10"></th>
            {MAANEDER.map(m => (
              <React.Fragment key={m.key}>
                <th className="px-2 py-1.5 text-right text-[9px] uppercase tracking-wider text-sky-400/60 font-semibold border-l border-white/[0.06]">B</th>
                <th className="px-2 py-1.5 text-right text-[9px] uppercase tracking-wider text-emerald-400/60 font-semibold">R</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {konsulenter.length === 0 ? (
            <tr>
              <td colSpan={1 + MAANEDER.length * 2} className="px-4 py-8 text-center text-white/30 text-sm">
                Ingen {entityLabel} fundet
              </td>
            </tr>
          ) : (
            <>
              {konsulenter.map(k => {
                // Realiseret: brug propsen hvis givet, ellers fall-back til den globale provision-tabel
                const realiseret = realisertData
                  ? (realisertData[normalizeName(k.name)] || realisertData[shortKey(k.name)] || {})
                  : lookupProvision(k.name);
                const budget = budgetData
                  ? (budgetData[normalizeName(k.name)] || budgetData[shortKey(k.name)] || {})
                  : {};
                return (
                  <tr key={k.name} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`provision-row-${k.name}`}>
                    <td className="px-4 py-2.5 text-white/85 font-medium sticky left-0 bg-[#13151a] z-10">{k.name}</td>
                    {MAANEDER.map(m => {
                      const r = realiseret[m.key];
                      const b = budget[m.key];
                      const formattedR = formatProvisionAmount(r);
                      const formattedB = formatProvisionAmount(b);
                      return (
                        <React.Fragment key={m.key}>
                          <td className={`px-2 py-2.5 text-right tabular-nums border-l border-white/[0.04] ${formattedB === null ? 'text-white/15' : (b === 0 ? 'text-white/40' : 'text-sky-400/85 font-medium')}`}>
                            {formattedB === null ? '—' : formattedB}
                          </td>
                          <td className={`px-2 py-2.5 text-right tabular-nums ${formattedR === null ? 'text-white/15' : (r === 0 ? 'text-white/40' : 'text-emerald-400/85 font-medium')}`}>
                            {formattedR === null ? '—' : formattedR}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Sum-række — total pr. kolonne for alle konsulenter */}
              {(() => {
                const sums = {};
                MAANEDER.forEach(m => {
                  let budget = 0, realiseret = 0;
                  konsulenter.forEach(k => {
                    const rd = realisertData
                      ? (realisertData[normalizeName(k.name)] || realisertData[shortKey(k.name)] || {})
                      : lookupProvision(k.name);
                    const bd = budgetData
                      ? (budgetData[normalizeName(k.name)] || budgetData[shortKey(k.name)] || {})
                      : {};
                    if (typeof rd[m.key] === 'number') realiseret += rd[m.key];
                    if (typeof bd[m.key] === 'number') budget += bd[m.key];
                  });
                  sums[m.key] = { budget, realiseret };
                });
                return (
                  <tr className="border-t-2 border-white/15 bg-white/[0.03] font-semibold" data-testid="provision-sum-row">
                    <td className="px-4 py-3 text-white/90 uppercase tracking-wider text-[10px] font-bold sticky left-0 bg-[#1a1c20] z-10">Total</td>
                    {MAANEDER.map(m => {
                      const s = sums[m.key];
                      const formattedR = s.realiseret > 0 ? formatProvisionAmount(s.realiseret) : null;
                      const formattedB = s.budget > 0 ? formatProvisionAmount(s.budget) : null;
                      return (
                        <React.Fragment key={m.key}>
                          <td className={`px-2 py-3 text-right tabular-nums border-l border-white/[0.06] ${formattedB === null ? 'text-white/30' : 'text-sky-400 font-bold'}`}>
                            {formattedB === null ? '—' : formattedB}
                          </td>
                          <td className={`px-2 py-3 text-right tabular-nums ${formattedR === null ? 'text-white/30' : 'text-emerald-400 font-bold'}`}>
                            {formattedR === null ? '—' : formattedR}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })()}
            </>
          )}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-white/[0.06] text-[11px] text-white/40 flex items-center gap-4">
        <span><span className="text-sky-400/70 font-semibold">B</span> = Budget</span>
        <span><span className="text-emerald-400/70 font-semibold">R</span> = Realiseret</span>
        <span className="ml-auto">{konsulenter.length} {entityLabel}</span>
      </div>
    </div>
  );
};

export default function Fastloennede() {
  const [activeTab, setActiveTab] = useState('ledelsen');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [konsulenterForProvision, setKonsulenterForProvision] = useState([]);
  const [montoererForProvision, setMontoererForProvision] = useState([]);
  const [loadingProvisionList, setLoadingProvisionList] = useState(false);

  const tabs = [
    { id: 'ledelsen', label: 'Ledelsen' },
    { id: 'konsulenter', label: 'Konsulenter' },
    { id: 'montoerer', label: 'Montører' },
    { id: 'freelancer', label: 'Freelancer' },
    { id: 'provision', label: 'Provision konsulenter' },
    { id: 'provision_montoerer', label: 'Provision montører' },
  ];

  const fetchData = useCallback(async () => {
    if (activeTab === 'provision' || activeTab === 'provision_montoerer') {
      // Provision: hent personalelisten (uden løndata) til at vise rækker i tabellen
      const endpoint = activeTab === 'provision' ? '/salaries/konsulenter' : '/salaries/montoerer';
      setLoading(false);
      setLoadingProvisionList(true);
      try {
        const res = await api.get(endpoint);
        if (activeTab === 'provision') {
          setKonsulenterForProvision(res.data.employees || []);
        } else {
          setMontoererForProvision(res.data.employees || []);
        }
      } catch (err) {
        console.error('Failed to fetch list for provision', err);
        if (activeTab === 'provision') setKonsulenterForProvision([]);
        else setMontoererForProvision([]);
      } finally {
        setLoadingProvisionList(false);
      }
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/salaries/${activeTab}`);
      setEmployees(res.data.employees || []);
    } catch (err) { console.error('Failed to fetch salaries', err); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (name, salary, startDate, endDate, weeklyHours) => {
    await api.post(`/salaries/${activeTab}`, { name, salary, start_date: startDate, end_date: endDate, weekly_hours: weeklyHours });
    await fetchData();
  };

  const handleDelete = async (name) => {
    await api.delete(`/salaries/${activeTab}`, { data: { name } });
    await fetchData();
  };

  const handleUpdate = async (name, fields) => {
    await api.patch(`/salaries/${activeTab}`, { name, ...fields });
    await fetchData();
  };

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="fastloennede-page">
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-4">
          <Navigation />
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#1E6F7A]" />
            Kørende salg
          </h1>
        </div>
      </header>

      <div className={`p-4 md:p-6 ${(activeTab === 'provision' || activeTab === 'provision_montoerer' || activeTab === 'freelancer') ? '' : 'max-w-5xl mx-auto'}`}>
        <div className="flex gap-1 mb-6 bg-white/5 rounded-lg p-1 w-fit" data-testid="salary-tabs">
          {tabs.map(tab => (
            <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-[#1E6F7A] text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}>{tab.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'provision' ? (
          <ProvisionTable konsulenter={konsulenterForProvision} loading={loadingProvisionList} entityLabel="konsulenter" />
        ) : activeTab === 'provision_montoerer' ? (
          (() => {
            // Realiseret = manuelt indtastet MONTOER_PROVISION_REALISERET_FULL (med fornavn+efternavn fallback)
            const realisertLookup = {};
            montoererForProvision.forEach(m => {
              const full = normalizeName(m.name);
              const data = lookupMontoerProvision(m.name);
              if (data && Object.keys(data).length > 0) realisertLookup[full] = data;
            });
            return (
              <ProvisionTable
                konsulenter={montoererForProvision}
                loading={loadingProvisionList}
                entityLabel="montører"
                realisertData={realisertLookup}
              />
            );
          })()
        ) : activeTab === 'freelancer' ? (
          (() => {
            // Brug freelancers nuværende løn som budget i alle 12 måneder
            const budgetData = {};
            employees.forEach(e => {
              const key = normalizeName(e.name);
              const monthly = {};
              MAANEDER.forEach(m => { monthly[m.key] = e.salary || 0; });
              budgetData[key] = monthly;
            });
            // Realiseret = manuelt indtastet tabel (med fornavn+efternavn fallback)
            const realisertLookup = {};
            employees.forEach(e => {
              const full = normalizeName(e.name);
              const short = shortKey(e.name);
              const data = FREELANCER_REALISERET[full] || FREELANCER_REALISERET_SHORT[short];
              if (data) realisertLookup[full] = data;
            });
            return (
              <ProvisionTable
                konsulenter={employees}
                loading={false}
                entityLabel="freelancere"
                budgetData={budgetData}
                realisertData={realisertLookup}
              />
            );
          })()
        ) : (
          <SalaryTable employees={employees} onAdd={handleAdd} onDelete={handleDelete} onUpdate={handleUpdate} showHours={activeTab === 'konsulenter' || activeTab === 'montoerer'} />
        )}
      </div>
    </div>
  );
}
